import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ButtonControl, DialogControl, FieldControl, IconControl, Loading, SidePanelFormControl } from "spark-core-dx/components";
import CardGridControl from "../../resource/components/CardGridControl";
import { useResourceDomainContext } from "../../resource/ResourceDomainProvider";
import { RESOURCES_TRADING_PARTNERS_PATH } from "../../resource/resourceRoutes";
import { EDI_TRADING_PARTNER_ICON } from "./EdiTradingPartnerConnectivityManage";
import "../../resource/workspace/ResourceWorkspace.scss";

const textField = (fieldName, placeholder, ariaLabel) => ({
  FieldName: fieldName,
  DisplayName: "",
  FieldType: "TextBox",
  PlaceHolder: placeholder,
  AriaLabel: ariaLabel
});

const getValue = (item, ...keys) => {
  if (!item) return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(item, key) && item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }
  return undefined;
};

const getString = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return value === undefined || value === null ? "" : String(value);
};

const getArray = (item, ...keys) => {
  const value = getValue(item, ...keys);
  return Array.isArray(value) ? value : [];
};

const getStringArray = (item, ...keys) => {
  const value = getValue(item, ...keys);
  if (Array.isArray(value)) return value.filter((entry) => entry !== undefined && entry !== null && String(entry).trim());
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter((entry) => entry !== undefined && entry !== null && String(entry).trim());
    } catch {
      return trimmed.split(",").map((entry) => entry.trim()).filter(Boolean);
    }
  }
  return [];
};

const firstItem = (value) => {
  if (Array.isArray(value)) return value[0] || null;
  if (Array.isArray(value?.Items)) return value.Items[0] || null;
  if (Array.isArray(value?.items)) return value.items[0] || null;
  return value || null;
};

const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");
const getOperationItems = (response) => getArray(response, "Items", "items", "Data", "data");
const getOperationSuccess = (response) => response?.Success !== false && response?.success !== false;
const getMetadataFields = (metadata) => getArray(metadata, "Fields", "fields");
const getGroupName = (group) => getString(group, "GroupName", "groupName", "DisplayName", "displayName", "Name", "name") || "Trading Partner Group";
const getGroupCode = (group) => getString(group, "GroupCode", "groupCode", "GroupIdentifier", "groupIdentifier", "TradingPartnerGroupId", "tradingPartnerGroupId");
const getPartnerName = (partner) => getString(partner, "PartnerName", "partnerName", "DisplayName", "displayName", "Name", "name") || "Trading Partner";
const getPartnerCode = (partner) => getString(partner, "PartnerCode", "partnerCode", "PartnerIdentifier", "partnerIdentifier", "TradingPartnerId", "tradingPartnerId");
const getPartnerExternalId = (partner) => getString(partner, "ExternalPartnerId", "externalPartnerId", "ExternalId", "externalId");
const getDescription = (item) => getString(item, "Description", "description");

const getGroupPartnerCount = (group) => {
  const members = getArray(group, "TradingPartnerMembers", "tradingPartnerMembers", "Members", "members");
  if (members.length > 0) return members.length;
  return getStringArray(group, "TradingPartnerIds", "tradingPartnerIds", "PartnerIds", "partnerIds").length;
};

const getGroupPartnerIds = (group) => {
  const memberIds = getArray(group, "TradingPartnerMembers", "tradingPartnerMembers", "Members", "members")
    .map((member) => getString(member, "TradingPartnerId", "tradingPartnerId", "PartnerId", "partnerId"))
    .filter(Boolean);
  if (memberIds.length > 0) return memberIds;
  return getStringArray(group, "TradingPartnerIds", "tradingPartnerIds", "PartnerIds", "partnerIds");
};

const createGroupDraft = () => ({
  IsNew: true,
  IsActive: true,
  GroupName: "",
  GroupCode: "",
  TradingPartnerMembers: []
});

const filterRows = (rows, searchText) => {
  const normalizedSearch = (searchText || "").trim().toLowerCase();
  if (!normalizedSearch) return rows;
  return rows.filter((row) => `${getGroupName(row)} ${getGroupCode(row)} ${getDescription(row)}`.toLowerCase().includes(normalizedSearch));
};

const filterPartners = (rows, searchText) => {
  const normalizedSearch = (searchText || "").trim().toLowerCase();
  if (!normalizedSearch) return rows;
  return rows.filter((row) => `${getPartnerName(row)} ${getPartnerCode(row)} ${getPartnerExternalId(row)} ${getDescription(row)}`.toLowerCase().includes(normalizedSearch));
};

const partnerSelectorMetadata = {
  Name: "EdiTradingPartnerGroupMemberSelector",
  DisplayName: "Trading Partner",
  Fields: [
    { FieldName: "PartnerCode", DisplayName: "Partner Code", ShowOnCard: true, DisplaySequence: 1 },
    { FieldName: "ExternalPartnerId", DisplayName: "External ID", ShowOnCard: true, DisplaySequence: 2 }
  ]
};

const partnerSelectorCardFields = partnerSelectorMetadata.Fields;

const withTimeout = (promise, label, timeoutMs = 12000) => new Promise((resolve, reject) => {
  const timeout = window.setTimeout(() => reject(new Error(`${label} did not finish.`)), timeoutMs);
  Promise.resolve(promise)
    .then((value) => {
      window.clearTimeout(timeout);
      resolve(value);
    })
    .catch((error) => {
      window.clearTimeout(timeout);
      reject(error);
    });
});

const EdiTradingPartnerGroupsManage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resourceDomainContext = useResourceDomainContext();
  const groupManager = resourceDomainContext?.managers?.ediTradingPartnerGroup;
  const partnerManager = resourceDomainContext?.managers?.ediTradingPartner;
  const groupManagerRef = useRef(groupManager);
  const partnerManagerRef = useRef(partnerManager);
  const [groups, setGroups] = useState([]);
  const [partners, setPartners] = useState([]);
  const [groupMetadata, setGroupMetadata] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [partnerSelectorGroup, setPartnerSelectorGroup] = useState(null);
  const [partnerSelectorIds, setPartnerSelectorIds] = useState([]);
  const [partnerSelectorSearchText, setPartnerSelectorSearchText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const searchParams = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const backUrl = searchParams.get("backUrl");
  const hasGroupManager = Boolean(groupManager);

  useEffect(() => {
    groupManagerRef.current = groupManager;
  }, [groupManager]);

  useEffect(() => {
    partnerManagerRef.current = partnerManager;
  }, [partnerManager]);

  const loadRows = useCallback(async () => {
    const activeGroupManager = groupManagerRef.current;
    const activePartnerManager = partnerManagerRef.current;

    if (!activeGroupManager) {
      setLoading(false);
      setError("Trading partner group manager is not available from the Resource domain.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const partnerListRequest = activePartnerManager
        ? withTimeout(activePartnerManager.Query?.({ model: "x => x.IsDeleted == false" }), "Trading partner list")
        : Promise.resolve({ Success: true, Items: [] });
      const [groupMetadataResult, groupResult, partnerResult] = await Promise.all([
        withTimeout(activeGroupManager.GetMetadata?.({}), "Trading partner group metadata"),
        withTimeout(activeGroupManager.Query?.({ model: "x => x.IsDeleted == false" }), "Trading partner group list"),
        partnerListRequest
      ]);

      setGroupMetadata(firstItem(groupMetadataResult));
      setGroups(getOperationItems(groupResult).filter((group) => getValue(group, "IsDeleted", "isDeleted") !== true));
      setPartners(getOperationItems(partnerResult).filter((partner) => getValue(partner, "IsDeleted", "isDeleted") !== true));
    } catch (loadError) {
      setError(loadError?.message || "Trading partner groups could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [hasGroupManager, loadRows]);

  const saveGroup = useCallback(async (group) => {
    setSaveError("");
    const activeGroupManager = groupManagerRef.current;
    const response = await activeGroupManager?.saveItem(group);
    if (!getOperationSuccess(response)) {
      setSaveError("Trading partner group could not be saved.");
      return;
    }
    setEditingGroup(null);
    await loadRows();
  }, [loadRows]);

  const deleteGroup = useCallback(async () => {
    if (!deletingGroup) return;

    setSaveError("");
    const activeGroupManager = groupManagerRef.current;
    const groupId = getId(deletingGroup);
    const response = await activeGroupManager?.saveItem({
      ...deletingGroup,
      IsActive: false,
      IsDeleted: true,
      IsNew: false
    });

    if (!response || !getOperationSuccess(response)) {
      setSaveError("Trading partner group could not be deleted.");
      return;
    }

    setDeletingGroup(null);
    setGroups((currentGroups) => currentGroups.filter((group) => getId(group) !== groupId));
    await loadRows();
  }, [deletingGroup, loadRows]);

  const openPartnerSelector = useCallback((group) => {
    setPartnerSelectorGroup(group);
    setPartnerSelectorIds(getGroupPartnerIds(group));
    setPartnerSelectorSearchText("");
  }, []);

  const closePartnerSelector = useCallback(() => {
    setPartnerSelectorGroup(null);
    setPartnerSelectorIds([]);
    setPartnerSelectorSearchText("");
  }, []);

  const savePartnerSelector = useCallback(async () => {
    if (!partnerSelectorGroup) return;
    setSaveError("");
    const activeGroupManager = groupManagerRef.current;
    const existingMembers = getArray(partnerSelectorGroup, "TradingPartnerMembers", "tradingPartnerMembers", "Members", "members");
    const nextMembers = partnerSelectorIds.map((partnerId) => {
      const existingMember = existingMembers.find((member) =>
        String(getString(member, "TradingPartnerId", "tradingPartnerId", "PartnerId", "partnerId")) === String(partnerId));
      return existingMember || {
        IsNew: true,
        IsActive: true,
        TradingPartnerId: partnerId,
        Role: ""
      };
    });
    const response = await activeGroupManager?.saveItem({
      ...partnerSelectorGroup,
      TradingPartnerMembers: nextMembers
    });
    if (!getOperationSuccess(response)) {
      setSaveError("Trading partner group members could not be saved.");
      return;
    }
    closePartnerSelector();
    await loadRows();
  }, [closePartnerSelector, loadRows, partnerSelectorGroup, partnerSelectorIds]);

  const filteredGroups = useMemo(
    () => filterRows(groups, searchText),
    [groups, searchText]
  );

  const filteredPartners = useMemo(
    () => filterPartners(partners, partnerSelectorSearchText),
    [partners, partnerSelectorSearchText]
  );

  const activeGroupCount = useMemo(
    () => groups.filter((group) => getValue(group, "IsActive", "isActive") !== false).length,
    [groups]
  );

  const goBack = useCallback(() => {
    navigate(backUrl || RESOURCES_TRADING_PARTNERS_PATH);
  }, [backUrl, navigate]);

  const searchControl = (
    <FieldControl
      value={searchText}
      onChange={(value) => setSearchText(value ?? "")}
      fieldMetadata={textField("TradingPartnerGroupSearch", "Search groups...", "Search groups")}
    />
  );

  const emptyOrLoadingContent = () => {
    if (loading) {
      return (
        <div className="resource-workspace-empty-state resource-workspace-empty-state--compact">
          <Loading />
          <span>Loading trading partner groups.</span>
        </div>
      );
    }

    if (error) {
      return <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{error}</div>;
    }

    return (
      <div className="resource-workspace-empty-state resource-workspace-empty-state--compact">
        <span>No trading partner groups have been configured.</span>
      </div>
    );
  };

  const groupRows = filteredGroups.length > 0 ? (
    <div className="resource-workspace-record-list">
      {filteredGroups.map((group) => (
        <article key={getId(group)} className="resource-workspace-configuration-linked-row resource-workspace-entity-object-card resource-workspace-edi-trading-partner-group-row">
          <span className="resource-workspace-configuration-linked-copy resource-workspace-entity-object-card__copy">
            <strong>{getGroupName(group)}</strong>
            <small>{getGroupCode(group) || "No group code"}</small>
          </span>
          <dl className="resource-workspace-entity-object-card__facts">
            <div>
              <dt>Status</dt>
              <dd>{getValue(group, "IsActive", "isActive") === false ? "Inactive" : "Active"}</dd>
            </div>
            <div>
              <dt>Partners</dt>
              <dd>{getGroupPartnerCount(group)} partner{getGroupPartnerCount(group) === 1 ? "" : "s"} in group</dd>
            </div>
          </dl>
          <span className="resource-workspace-entity-object-card__actions">
              <ButtonControl type="standard-small" title={`Manage partners for ${getGroupName(group)}`} aria-label={`Manage partners for ${getGroupName(group)}`} onClick={() => openPartnerSelector(group)}>
                <IconControl iconKey="checklist" title={`Manage partners for ${getGroupName(group)}`} width="1rem" height="1rem" />
              </ButtonControl>
            <ButtonControl type="standard-small" title={`Edit ${getGroupName(group)}`} aria-label={`Edit ${getGroupName(group)}`} onClick={() => setEditingGroup(group)}>
              <IconControl iconKey="edit" title={`Edit ${getGroupName(group)}`} width="1rem" height="1rem" />
            </ButtonControl>
            <ButtonControl className="resource-workspace-row-action--danger" type="standard-small" title={`Delete ${getGroupName(group)}`} aria-label={`Delete ${getGroupName(group)}`} onClick={() => setDeletingGroup(group)}>
              <IconControl iconKey="delete" title={`Delete ${getGroupName(group)}`} width="1rem" height="1rem" />
            </ButtonControl>
          </span>
        </article>
      ))}
    </div>
  ) : emptyOrLoadingContent();

  return (
    <main className="resource-workspace resource-workspace-edi-trading-partners" aria-label="EDI trading partner group configuration">
      <section className="resource-workspace-screen">
        <div className="resource-workspace-configuration-screen">
          <div className="resource-workspace-configuration-management-header">
            <div className="resource-workspace-configuration-management-title">
              <span className="resource-workspace-header-icon" aria-hidden="true">
                <IconControl iconKey={EDI_TRADING_PARTNER_ICON} title="Trading Partner Groups" width="2.25rem" height="2.25rem" />
              </span>
              <span>
                <span className="resource-workspace-section-eyebrow">EDI</span>
                <h1>Trading Partner Groups</h1>
              </span>
            </div>
          </div>

          <div className="resource-workspace-configuration-command-bar" role="toolbar" aria-label="Trading partner group actions">
            <ButtonControl type="standard-small" onClick={loadRows} disabled={loading}>
              <IconControl iconKey="refresh" title="Refresh" width="1rem" height="1rem" />
              Refresh
            </ButtonControl>
            <ButtonControl type="standard-small" onClick={() => setEditingGroup(createGroupDraft())}>
              <IconControl iconKey="add" title="Add Group" width="1rem" height="1rem" />
              Add Group
            </ButtonControl>
            <ButtonControl type="standard-small" className="resource-workspace-command-bar-action resource-workspace-command-bar-action--right" onClick={goBack}>
              <IconControl iconKey="undo" title={backUrl ? "Back to Trading Partners" : "Trading Partners"} width="1rem" height="1rem" />
              {backUrl ? "Back to Trading Partners" : "Trading Partners"}
            </ButtonControl>
          </div>

          <div className="resource-workspace-linked-item-layout resource-workspace-edi-trading-partners__content">
            <section className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-entity-object-list-section">
              <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
                <div>
                  <h2>Partner Groups</h2>
                  <p>Configured EDI partner groups that can be assigned to inbound or outbound document flows.</p>
                </div>
                <div className="resource-workspace-section-actions">
                  <span className="resource-workspace-edi-trading-partners__count">{activeGroupCount} active / {groups.length} total</span>
                  {searchControl}
                </div>
              </div>
              {groupRows}
            </section>
          </div>
        </div>
      </section>

      {saveError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{saveError}</div> : null}
      <SidePanelFormControl
        open={Boolean(editingGroup)}
        title={getId(editingGroup) ? `Edit Group: ${getGroupName(editingGroup)}` : "Add Trading Partner Group"}
        metadata={groupMetadata || { Name: "EdiTradingPartnerGroup", DisplayName: "EDI Trading Partner Group", Fields: [] }}
        formFields={getMetadataFields(groupMetadata)}
        itemData={editingGroup}
        setItemData={setEditingGroup}
        setOpenEditSidePanel={(isOpen) => {
          if (!isOpen) setEditingGroup(null);
        }}
        onUpdateSidePanel={() => {}}
        onCancel={() => setEditingGroup(null)}
        onSave={saveGroup}
        saveButtonText="Save"
        viewOnly={!groupMetadata}
      />
      <DialogControl
        title={partnerSelectorGroup ? `Select Partners: ${getGroupName(partnerSelectorGroup)}` : "Select Partners"}
        openDialog={Boolean(partnerSelectorGroup)}
        okText="Save Partners"
        cancelText="Cancel"
        onOk={savePartnerSelector}
        onCancel={closePartnerSelector}
        onX={closePartnerSelector}
      >
        <div className="resource-workspace-edi-trading-partners__selector">
          <p className="resource-workspace-edi-trading-partners__selector-note">Select or deselect partners included in this group.</p>
          <FieldControl
            value={partnerSelectorSearchText}
            onChange={(value) => setPartnerSelectorSearchText(value ?? "")}
            fieldMetadata={textField("TradingPartnerGroupMemberSearch", "Search trading partners...", "Search trading partners")}
          />
          <CardGridControl
            rows={filteredPartners}
            metadata={partnerSelectorMetadata}
            cardFields={partnerSelectorCardFields}
            rowKey={getId}
            selectedRowKeys={partnerSelectorIds}
            onSelectedRowKeysChange={setPartnerSelectorIds}
            selectable
            multiSelect
            searchable={false}
            activeFilterEnabled={false}
            disableInlineEdit
            disableCardClickEdit
            minCardWidth="14rem"
            emptyContent={partners.length ? "No trading partners match the current search." : "No trading partners have been configured."}
            cardTitleResolver={getPartnerName}
          />
        </div>
      </DialogControl>
      <DialogControl
        title="Delete Trading Partner Group"
        openDialog={Boolean(deletingGroup)}
        okText="Delete"
        cancelText="Cancel"
        onOk={deleteGroup}
        onCancel={() => setDeletingGroup(null)}
        onX={() => setDeletingGroup(null)}
      >
        <p>Deleting <strong>{getGroupName(deletingGroup)}</strong> will remove this group from available EDI partner scope assignment.</p>
        <p>Partners in the group are not deleted.</p>
      </DialogControl>
    </main>
  );
};

export default EdiTradingPartnerGroupsManage;
